/**
 * ComposeOptionalDetails Component
 * Service type, budget, and timeline in a 3-column grid
 */

import { GRIDS, SPACING } from '@/design-system';
import { useLocalizations } from '@/hooks/useLocalizations';
import { LComponents } from '@/components/i18n';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ComposeFormData } from './types';

interface ComposeOptionalDetailsProps {
  formData: ComposeFormData;
  onFormChange: (updates: Partial<ComposeFormData>) => void;
}

export function ComposeOptionalDetails({ formData, onFormChange }: ComposeOptionalDetailsProps) {
  const { lComponents } = useLocalizations();

  return (
    <div className={GRIDS.formGridSmTrio}>
      {/* Service Type */}
      <div className={SPACING.formFieldGap}>
        <Label htmlFor="service_type">
          <LComponents id="compose_message.labels.service_type" defaultValue='Service Type' />
        </Label>
        <Select
          value={formData.service_type}
          onValueChange={(value) => onFormChange({ service_type: value })}
        >
          <SelectTrigger id="service_type" className="h-10">
            <SelectValue placeholder={lComponents('compose_message.placeholders.select_service_type', { defaultValue: 'Select service type' })} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Accounting"><LComponents id="compose_message.service_types.accounting" defaultValue='Accounting Services' /></SelectItem>
            <SelectItem value="Anti-Money Laundering"><LComponents id="compose_message.service_types.anti_money_laundering" defaultValue='Anti-Money Laundering Services' /></SelectItem>
            <SelectItem value="Company Formation / Incorporation"><LComponents id="compose_message.service_types.company_formation" defaultValue='Company Formation Services' /></SelectItem>
            <SelectItem value="Compliance and Regulatory"><LComponents id="compose_message.service_types.compliance_regulatory" defaultValue='Compliance & Regulatory Services' /></SelectItem>
            <SelectItem value="Corporate Services"><LComponents id="compose_message.service_types.corporate_services" defaultValue='Corporate Services' /></SelectItem>
            <SelectItem value="Corporate Tax"><LComponents id="compose_message.service_types.corporate_tax" defaultValue='Corporate Tax Services' /></SelectItem>
            <SelectItem value="Cross-Border Estates"><LComponents id="compose_message.service_types.cross_border_estates" defaultValue='Cross Border Estates' /></SelectItem>
            <SelectItem value="Estate Planning"><LComponents id="compose_message.service_types.estate_planning" defaultValue='Estate Planning Services' /></SelectItem>
            <SelectItem value="Family Office"><LComponents id="compose_message.service_types.family_office" defaultValue='Family Office Services' /></SelectItem>
            <SelectItem value="Immigration and Visa"><LComponents id="compose_message.service_types.immigration_visa" defaultValue='Immigration and Visa Services' /></SelectItem>
            <SelectItem value="International Tax"><LComponents id="compose_message.service_types.international_tax" defaultValue='International Tax Services' /></SelectItem>
            <SelectItem value="Personal Tax"><LComponents id="compose_message.service_types.personal_tax" defaultValue='Individual Tax Services' /></SelectItem>
            <SelectItem value="Succession Planning"><LComponents id="compose_message.service_types.succession_planning" defaultValue='Succession Planning Services' /></SelectItem>
            <SelectItem value="Tax Advisory"><LComponents id="compose_message.service_types.tax_advisory" defaultValue='Tax Advisory Services' /></SelectItem>
            <SelectItem value="Tax Compliance"><LComponents id="compose_message.service_types.tax_compliance" defaultValue='Tax Compliance' /></SelectItem>
            <SelectItem value="Tax Dispute Resolution"><LComponents id="compose_message.service_types.tax_dispute_resolution" defaultValue='Tax Dispute Resolution' /></SelectItem>
            <SelectItem value="Tax Planning"><LComponents id="compose_message.service_types.tax_planning" defaultValue='Tax Planning' /></SelectItem>
            <SelectItem value="Tax Services"><LComponents id="compose_message.service_types.tax_services" defaultValue='Tax Services' /></SelectItem>
            <SelectItem value="Transfer Pricing"><LComponents id="compose_message.service_types.transfer_pricing" defaultValue='Transfer Pricing' /></SelectItem>
            <SelectItem value="Trusts Planning and Formation"><LComponents id="compose_message.service_types.trusts_planning" defaultValue='Trusts Planning' /></SelectItem>
            <SelectItem value="Wealth Management"><LComponents id="compose_message.service_types.wealth_management" defaultValue='Wealth Management' /></SelectItem>
            <SelectItem value="Wills and Probate"><LComponents id="compose_message.service_types.wills_probate" defaultValue='Wills and Probate' /></SelectItem>
            <SelectItem value="Other"><LComponents id="compose_message.service_types.other" defaultValue='Other Services' /></SelectItem>
          </SelectContent>
        </Select>
      </div>
      {/* Budget Range */}
      <div className={SPACING.formFieldGap}>
        <Label htmlFor="budget_range">
          <LComponents id="compose_message.labels.budget_range" defaultValue='Budget Range' />
        </Label>
        <Select
          value={formData.budget_range}
          onValueChange={(value) => onFormChange({ budget_range: value })}
        >
          <SelectTrigger id="budget_range" className="h-10">
            <SelectValue placeholder={lComponents('compose_message.placeholders.select_budget_range', { defaultValue: 'Select budget range' })} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="< $5,000"><LComponents id="compose_message.budget_options.less_5k" defaultValue='Under $5K' /></SelectItem>
            <SelectItem value="$5,000 - $15,000"><LComponents id="compose_message.budget_options.5k_15k" defaultValue='$5K - $15K' /></SelectItem>
            <SelectItem value="$15,000 - $50,000"><LComponents id="compose_message.budget_options.15k_50k" defaultValue='$15K - $50K' /></SelectItem>
            <SelectItem value="$50,000 - $100,000"><LComponents id="compose_message.budget_options.50k_100k" defaultValue='$50K - $100K' /></SelectItem>
            <SelectItem value="> $100,000"><LComponents id="compose_message.budget_options.more_100k" defaultValue='$100K+' /></SelectItem>
          </SelectContent>
        </Select>
      </div>
      {/* Timeline */}
      <div className={SPACING.formFieldGap}>
        <Label htmlFor="timeline">
          <LComponents id="compose_message.labels.timeline" defaultValue='Timeline' />
        </Label>
        <Select
          value={formData.timeline}
          onValueChange={(value) => onFormChange({ timeline: value })}
        >
          <SelectTrigger id="timeline" className="h-10">
            <SelectValue placeholder={lComponents('compose_message.placeholders.select_timeline', { defaultValue: 'Select timeline' })} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Urgent (< 1 week)"><LComponents id="compose_message.timeline_options.urgent" defaultValue='Urgent' /></SelectItem>
            <SelectItem value="1-2 weeks"><LComponents id="compose_message.timeline_options.1_2_weeks" defaultValue='1 to 2 Weeks' /></SelectItem>
            <SelectItem value="2-4 weeks"><LComponents id="compose_message.timeline_options.2_4_weeks" defaultValue='2 to 4 Weeks' /></SelectItem>
            <SelectItem value="1-3 months"><LComponents id="compose_message.timeline_options.1_3_months" defaultValue='1 to 3 Months' /></SelectItem>
            <SelectItem value="3+ months"><LComponents id="compose_message.timeline_options.3_plus_months" defaultValue='Over 3 Months' /></SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
